import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../components/ui/Modal.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Select } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { formatDate, formatCurrency } from '../../lib/format.js'

/*
 * Company details (admin) — inventory §3.1 Info sub-tab. Read-only company
 * fields + the per-company USERS table with role management: add a role
 * (POST /admin/companies/:cid/users/:uid/roles/:role) and remove a role chip
 * (DELETE …/:role). super-administrateur is global and not assignable here.
 */
export default function CompanyDetailsModal({ companyId, onClose }) {
  const { t } = useTranslation()
  const { toast } = useFeedback()
  const [company, setCompany] = useState(null)
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [addFor, setAddFor] = useState(null) // user id we're adding a role to
  const [showBilling, setShowBilling] = useState(false)
  const [billing, setBilling] = useState(null)
  const [plans, setPlans] = useState([])
  const [planSel, setPlanSel] = useState('')
  const [savingPlan, setSavingPlan] = useState(false)

  const load = useCallback(() => {
    Promise.all([
      api.get(`/admin/companies/${companyId}`),
      api.get('/roles/').catch(() => ({ data: { roles: [] } })),
      api.get(`/admin/billing/companies/${companyId}`).catch(() => ({ data: {} })),
      api.get('/admin/billing/plans').catch(() => ({ data: { plans: [] } })),
    ]).then(([c, r, b, p]) => {
      setCompany(c.data?.company || c.data)
      setRoles((r.data?.roles || []).filter((x) => (x.name || x) !== 'super-administrateur'))
      const bs = b.data?.billing_status || null
      setBilling(bs)
      setPlans((p.data?.plans || []).slice().sort((a, x) => (a.display_order ?? 0) - (x.display_order ?? 0)))
      setPlanSel(bs?.billing_plan_id ? String(bs.billing_plan_id) : '')
    }).finally(() => setLoading(false))
  }, [companyId])
  useEffect(() => { load() }, [load])

  const currentPlan = plans.find((p) => p.id === billing?.billing_plan_id)
  const changePlan = async () => {
    if (!planSel || Number(planSel) === billing?.billing_plan_id) return
    setSavingPlan(true)
    try {
      await api.post(`/admin/billing/companies/${companyId}/change-plan`, { plan_id: Number(planSel) })
      setBilling((b) => ({ ...(b || {}), billing_plan_id: Number(planSel) }))
      toast(t('admin.companies.planChanged'), 'success')
    } catch (e) { toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') } finally { setSavingPlan(false) }
  }

  // Optimistic local update (no full reload) — smoother, and the role select
  // doesn't detach mid-interaction.
  const patchUserRoles = (userId, fn) => setCompany((c) => ({ ...c, users: (c.users || []).map((u) => (u.id === userId ? { ...u, roles: fn(u.roles || []) } : u)) }))

  const addRole = async (userId, roleName) => {
    setAddFor(null)
    patchUserRoles(userId, (rs) => (rs.includes(roleName) ? rs : [...rs, roleName]))
    try { await api.post(`/admin/companies/${companyId}/users/${userId}/roles/${roleName}`); toast(t('admin.roles.added'), 'success') }
    catch (e) { patchUserRoles(userId, (rs) => rs.filter((r) => r !== roleName)); toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') }
  }
  const removeRole = async (userId, roleName) => {
    patchUserRoles(userId, (rs) => rs.filter((r) => r !== roleName))
    try { await api.delete(`/admin/companies/${companyId}/users/${userId}/roles/${roleName}`); toast(t('admin.roles.removed'), 'success') }
    catch (e) { patchUserRoles(userId, (rs) => [...rs, roleName]); toast(e?.response?.data?.error || t('admin.errorGeneric'), 'error') }
  }

  return (
    <Modal open onClose={onClose} maxWidth={720}>
      <div style={{ padding: 24, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexShrink: 0 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18 }}>{company?.name || t('admin.companies.details')}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="secondary" icon="invoice" onClick={() => setShowBilling(true)}>{t('admin.billingCycles.action')}</Button>
            <button onClick={onClose} aria-label={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}><Icon name="x" size={20} /></button>
          </div>
        </div>
        {loading ? <div style={{ padding: 30, color: 'var(--color-text-muted)' }}>…</div> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 18px', marginBottom: 20, background: 'var(--color-bg-lilac)', borderRadius: 12, padding: 16, flexShrink: 0 }}>
              <Info label={t('admin.companies.vat')} value={company.vat_number} />
              <Info label={t('admin.companies.country')} value={company.country_code} />
              {company.email && <Info label="Email" value={company.email} />}
              {company.phone && <Info label={t('parties.fields.phone')} value={company.phone} />}
              {(company.address || company.city) && <Info label={t('parties.fields.address')} value={`${company.address || ''} ${company.postal_code || ''} ${company.city || ''}`.trim()} />}
              <Info label={t('admin.companies.status')} value={company.is_active ? t('admin.active') : t('admin.inactive')} />
            </div>

            {/* Plan / subscription — view current + change. */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 20, flexShrink: 0, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 220 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('admin.companies.plan')}{currentPlan && <span style={{ marginLeft: 8, color: 'var(--color-primary)' }}>{currentPlan.name}</span>}</span>
                <Select value={planSel} onChange={(e) => setPlanSel(e.target.value)}>
                  {!billing && <option value="">{t('admin.companies.noPlan')}</option>}
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_free ? ` (${t('admin.plans.free')})` : ''}</option>)}
                </Select>
              </label>
              <Button variant="primary" icon="check" loading={savingPlan} disabled={!planSel || Number(planSel) === billing?.billing_plan_id} onClick={changePlan}>{t('admin.companies.changePlan')}</Button>
            </div>

            <h4 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10, flexShrink: 0 }}>{t('admin.roles.usersTitle')} {(company.users || []).length > 0 && <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>({company.users.length})</span>}</h4>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {(company.users || []).map((u) => {
                const userRoles = u.roles || []
                const available = roles.map((r) => r.name || r).filter((rn) => !userRoles.includes(rn))
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderTop: '1px solid var(--color-border-row)' }}>
                    <div style={{ minWidth: 180 }}>
                      <div style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 13.5 }}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{u.email}</div>
                    </div>
                    <div style={{ flex: 1, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {userRoles.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{t('admin.roles.none')}</span>}
                      {userRoles.map((rn) => (
                        <span key={rn} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 6px 3px 10px', borderRadius: 999, background: 'var(--color-bg-lilac-strong)', color: 'var(--color-primary)', fontSize: 12, fontWeight: 600 }}>
                          {rn}
                          <button onClick={() => removeRole(u.id, rn)} aria-label={t('admin.roles.remove')} title={t('admin.roles.remove')} style={{ display: 'inline-flex', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', padding: 0 }}><Icon name="x" size={13} /></button>
                        </span>
                      ))}
                      {addFor === u.id ? (
                        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                          {available.map((rn) => (
                            <button key={rn} onClick={() => addRole(u.id, rn)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, border: '1.5px solid var(--color-primary)', background: '#fff', color: 'var(--color-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              <Icon name="plus" size={12} /> {rn}
                            </button>
                          ))}
                          <button onClick={() => setAddFor(null)} title={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', display: 'inline-flex' }}><Icon name="x" size={14} /></button>
                        </span>
                      ) : available.length > 0 && (
                        <button onClick={() => setAddFor(u.id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, border: '1.5px dashed var(--color-border-strong)', background: '#fff', color: 'var(--color-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          <Icon name="plus" size={12} /> {t('admin.roles.add')}
                        </button>
                      )}
                    </div>
                    <Badge tone={u.is_active ? 'success' : 'neutral'}>{u.is_active ? t('admin.active') : t('admin.inactive')}</Badge>
                  </div>
                )
              })}
              {(company.users || []).length === 0 && <div style={{ padding: 18, color: 'var(--color-text-muted)', fontSize: 13.5 }}>{t('admin.roles.noUsers')}</div>}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="secondary" onClick={onClose}>{t('common.back')}</Button>
            </div>
          </>
        )}
      </div>
      {showBilling && <BillingCyclesModal companyId={companyId} companyName={company?.name} onClose={() => setShowBilling(false)} />}
    </Modal>
  )
}

const PAY_TONE = { succeeded: 'success', paid: 'success', pending: 'neutral', processing: 'info', failed: 'danger', canceled: 'danger' }

function BillingCyclesModal({ companyId, companyName, onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [cycles, setCycles] = useState(null)
  useEffect(() => {
    api.get(`/admin/billing/companies/${companyId}/invoices`)
      .then(({ data }) => setCycles(data?.invoices || [])).catch(() => setCycles([]))
  }, [companyId])

  return (
    <Modal open onClose={onClose} maxWidth={760}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '88vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px', borderBottom: '1px solid var(--color-border-row)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17 }}>{t('admin.billingCycles.title')}</h3>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{companyName}{cycles ? ` · ${t('admin.billingCycles.count', { count: cycles.length })}` : ''}</p>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {!cycles ? <div style={{ padding: 24, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
            : cycles.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('admin.billingCycles.empty')}</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {cycles.map((c) => (
                  <div key={c.id} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: 'var(--color-ink)', fontSize: 14 }}>{formatDate(c.period_start)} – {formatDate(c.period_end)}</span>
                      <Badge tone={PAY_TONE[c.payment_status] || 'neutral'}>{t(`admin.billingCycles.status.${c.payment_status}`, c.payment_status)}</Badge>
                      <div style={{ flex: 1 }} />
                      <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-ink)' }}>{formatCurrency(c.total_amount_incl_vat)}</span>
                    </div>
                    {/* Sent / received counts for the period */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                      <CountBox tone="info" icon="invoice" label={t('admin.billingCycles.sent')} count={c.outgoing_invoices_count} amount={c.outgoing_invoices_amount} />
                      <CountBox tone="neutral" icon="download" label={t('admin.billingCycles.received')} count={c.incoming_invoices_count} amount={c.incoming_invoices_amount} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', fontSize: 13 }}>
                      <Row label={t('admin.billingCycles.base')} value={formatCurrency(c.base_amount)} />
                      {c.extra_storage_tranches > 0 && <Row label={t('admin.billingCycles.storage', { n: c.extra_storage_tranches })} value={formatCurrency(c.extra_storage_amount)} />}
                      <Row label={t('admin.billingCycles.totalHt')} value={formatCurrency(c.total_amount_excl_vat)} />
                      <Row label={t('admin.billingCycles.vat')} value={formatCurrency(c.vat_amount)} />
                      {c.paid_at && <Row label={t('admin.billingCycles.paidAt')} value={formatDate(c.paid_at)} />}
                      {c.stripe_error_message && <Row label="Stripe" value={<span style={{ color: 'var(--color-danger)' }}>{c.stripe_error_message}</span>} />}
                    </div>
                    {c.invoice_id && (
                      <div style={{ marginTop: 12 }}>
                        <Button variant="secondary" icon="eye" onClick={() => navigate(`/app/invoices/${c.invoice_id}`)}>{t('admin.billingCycles.viewInvoice')}</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 22px', borderTop: '1px solid var(--color-border-row)' }}>
          <Button variant="secondary" onClick={onClose}>{t('common.close')}</Button>
        </div>
      </div>
    </Modal>
  )
}

function CountBox({ tone, icon, label, count, amount }) {
  const bg = tone === 'info' ? 'var(--color-bg-lilac)' : 'var(--color-bg-page)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: bg, borderRadius: 10 }}>
      <span style={{ color: 'var(--color-primary)' }}><Icon name={icon} size={18} /></span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{label}</div>
        <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-ink)' }}>{count ?? 0}</div>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{formatCurrency(amount)}</div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', textAlign: 'right' }}>{value}</span>
    </>
  )
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--color-ink)' }}>{value || '—'}</div>
    </div>
  )
}
